import Text from 'components/Text';
import { memo, useCallback, useMemo } from 'react';
import CalendarStyled, { fixedStyle } from '../styles';
import CalendarItem from './CalendarItem';
import Droppable from './DnD/Droppable';
import first from 'lodash/first';
import get from 'lodash/get';
import moment from 'utils/moment';
import type { Moment } from 'moment';
import { FORMAT_TIME } from 'utils/format';
import { IScheduleBooking } from 'services/types/shop';
import { ICalendarDndData, ICalendarViewColItem, ICalendarViewRowItem, ICalendarViewRowSpanInfoItem } from 'Calendar/types';
import { ANYONE_ID } from 'Calendar/anyone';
import clsx from 'clsx';
import { CellRange, toMinutesOfDay } from './multi-layout';

const TIME_VALUE_FORMAT = 'HH:mm';

export const convertCalendarTime = (rowTime: Moment) => {
  const minute = rowTime.minutes();
  if (minute > 0) return rowTime.format('mm');
  return rowTime.format(FORMAT_TIME);
};

type Props = {
  data: ICalendarViewRowItem,
  rowsSpanInfo: ICalendarViewRowSpanInfoItem[],
  isSingleCol?: boolean;
  distance: number;
  index: number;
  scheduleBooking: IScheduleBooking | null;
  setActiveTimeRow: (value: string) => void;
  paddingItems: JSX.Element | null
};
const CalendarViewRow = ({ data, rowsSpanInfo, distance, index, scheduleBooking, setActiveTimeRow, paddingItems }: Props) => {
  const { colData, rowTime } = data;

  const salonWorkingTime = useMemo(() => {
    const date = first(colData)?.rowTime.format('ddd');
    if (!date) return null;
    const dataWorkingTime = get(scheduleBooking, date);
    if (!dataWorkingTime) return null;

    const startTime = moment(dataWorkingTime.startTime, 'HH:mm');
    const endTime = moment(dataWorkingTime.endTime, 'HH:mm');
    return {
      startTime,
      endTime,
    };
  }, [data.rowTime, scheduleBooking, colData]);

  // Group span info by column once (O(1) lookup per cell instead of `.find()` over all spans).
  const spansByCol = useMemo(() => {
    const m = new Map<string, ICalendarViewRowSpanInfoItem[]>();
    rowsSpanInfo.forEach(it => {
      const list = m.get(it.colId);
      if (list) list.push(it); else m.set(it.colId, [it]);
    });
    return m;
  }, [rowsSpanInfo]);

  // Stable drop-data objects per column so memo(Droppable) can short-circuit across re-renders.
  const dropDataByCol = useMemo(() => {
    const m = new Map<string, ICalendarDndData>();
    colData.forEach(col => m.set(col.id, { rowId: data.id, colData: col, rowTime: data.rowTime }));
    return m;
  }, [colData, data.id, data.rowTime]);

  // Single "now" for the whole row instead of a fresh moment() per cell.
  const now = moment();

  // Stable CellRange per distinct rowSpan, so a merged cell hands memo(CalendarItem) the same object
  // across re-renders instead of a fresh one. A row has only a handful of distinct spans.
  const cellRangeBySpan = useMemo(() => {
    const start = toMinutesOfDay(data.rowTime);
    const cache = new Map<number, CellRange>();
    return (rowSpan: number): CellRange => {
      const cached = cache.get(rowSpan);
      if (cached) return cached;
      const range: CellRange = { start, span: rowSpan * distance };
      cache.set(rowSpan, range);
      return range;
    };
  }, [data.rowTime, distance]);

  const renderColumn = useCallback((col: ICalendarViewColItem, index: number) => {
    const dropId = data.id + '/' + col.id;
    const dropData = dropDataByCol.get(col.id) as ICalendarDndData;

    let disable = false;
    if (col.headerData.workingTime) {
      const colTime = moment(col.rowTime.format(TIME_VALUE_FORMAT), TIME_VALUE_FORMAT);
      const staffWorkingStartTime = moment(col.headerData.workingTime?.startTime, 'HH:mm');
      const staffWorkingEndTime = moment(col.headerData.workingTime?.endTime, 'HH:mm');

      const startWorkingDate = staffWorkingStartTime.isBefore(salonWorkingTime?.startTime) ? salonWorkingTime?.startTime : staffWorkingStartTime;
      const endWorkingDate = staffWorkingEndTime;

      const isBetweenWorkingHour = colTime.isSame(startWorkingDate) || colTime.isBetween(
        startWorkingDate,
        endWorkingDate
      );
      disable = !col.headerData.workingTime?.enable || !isBetweenWorkingHour;
    }

    const isFuture = col.rowTime.isSameOrAfter(now);

    if (!isFuture) {
      disable = true;
    }

    const orderGroup = spansByCol.get(col.id)?.find(it =>
      rowTime.isSameOrBefore(it?.rowEnd.value) &&
      col.data.some(i => it.orderIds.includes(i?.id?.toString())),
    );

    const isExact = !!orderGroup && orderGroup.rowStart.id === data.id;
    if (isExact) {
      // Time window the merged cell covers, taken from the row grid rather than from the items it
      // holds, so an item is placed against the slot the user actually sees.
      const cellRange = cellRangeBySpan(orderGroup?.rowSpan || 1);
      return (
        <CalendarStyled.TD anyone={col.id === ANYONE_ID} key={col.id + 'col'} rowSpan={orderGroup?.rowSpan || 1}
          className={clsx(
            col.rowTime.format('DD-MM-YYYY HH:mm'), 'CalendarStyled_isExact', dropId, col.id,
            index === 0 ? 'first-col' : '', col.id === ANYONE_ID ? 'fixed-time-anyone' : '',
            disable && 'disable',
          )}
        >
          <Droppable disabled={disable} rowSpan={orderGroup?.rowSpan || 1} key={dropId} id={dropId} data={dropData}>
            <CalendarItem disabled={disable} data={col.data} headerData={col.headerData} cellRange={cellRange} />
          </Droppable>
        </CalendarStyled.TD>
      );
    }
    const isBefore = !!orderGroup;

    if (isBefore) return <CalendarStyled.TD
      id={dropId}
      key={col.id + 'col'}
      className={clsx(
        col.rowTime.format('DD-MM-YYYY HH:mm'), 'CalendarStyled_isBefore', dropId, col.id, index === 0 ? 'first-col' : '',
        disable && 'disable',
      )}
    >
      <Droppable disabled={disable} key={dropId} id={dropId} data={dropData} />
    </CalendarStyled.TD>;

    return (
      <CalendarStyled.TD
        anyone={col.id === ANYONE_ID} id={dropId} key={col.id}
        className={clsx(
          col.rowTime.format('DD-MM-YYYY HH:mm'), `to_${col.nextRowTime}`, 'CalendarStyled_none',
          dropId, col.id,
          index === 0 ? 'first-col' : '',
          col.id === ANYONE_ID ? 'fixed-time-anyone' : '',
          disable && 'disable',
        )}
      >{
          <Droppable disabled={disable} key={dropId} id={dropId} data={dropData} />
        }</CalendarStyled.TD>
    );
  }, [data, rowTime, cellRangeBySpan, salonWorkingTime, spansByCol, dropDataByCol, now]);

  const time = useMemo(() => {
    return ({
      label: data.rowTime.format(FORMAT_TIME),
      strong: data.rowTime.format('mm') === '00',
      value: `to_${data.rowTime.format('hh-mm-A')}`,
    });
  }, [data.rowTime]);

  return (
    <CalendarStyled.TRow className={`${index === 0 ? 'first-row' : ''}`}>
      <CalendarStyled.TD id={`${time.value}_time`} className={`fixed-time-td ${time.value}_time`} fixed time style={fixedStyle}>
        <div className="box-time" style={{ textAlign: 'right' }}>
          <button type='button' onClick={() => setActiveTimeRow(time.value)}>
            <Text className='text-time' textAlign='right' variant='CAPTION_3' style={{ fontWeight: time.strong ? '700' : '400', fontSize: time.strong ? '0.85rem' : '0.75rem' }} >{time.label}</Text>
          </button>
        </div>
      </CalendarStyled.TD>
      {colData.length > 0 ? colData.map(renderColumn) : <CalendarStyled.TD className='first-col' />}
      {paddingItems}
    </CalendarStyled.TRow>
  );
};

export default memo(CalendarViewRow);
